import React from 'react';
import { Card, Button, Table, Badge } from 'react-bootstrap';
import { format, addDays, isSameDay } from 'date-fns';

export default function ManufacturingSchedule({
  orders,
  onUnschedule,
  onChangeDate,
  onMarkAsDone,
  toggleRow,
  expandedRows,
  renderExpandedRow,
  onSaveChanges,
  isOrderDirty,
  selectedWeek
}) {
  const [showProgress, setShowProgress] = React.useState(false);
  const today = React.useMemo(() => new Date(), []);
  const [copyStatus, setCopyStatus] = React.useState('default');

  const weekDates = React.useMemo(() => {
    const baseDate = new Date(selectedWeek + 'T00:00:00');
    const validDate = isNaN(baseDate.getTime()) ? new Date() : baseDate;

    return Array.from({ length: 6 }, (_, i) => addDays(validDate, i));
  }, [selectedWeek]);

  const ordersByDate = React.useMemo(() => {
    const map = Object.fromEntries(
      weekDates.map(d => [format(d, 'yyyy-MM-dd'), []])
    );

    orders.forEach(order => {
      const pDate = order.manufacturing?.orderEnteredProductionDate;
      if (!pDate) return;
      
      // Normalize date string to YYYY-MM-DD
      const dateStr = typeof pDate === 'string'
        ? pDate.split('T')[0]
        : format(new Date(pDate), 'yyyy-MM-dd');

      if (map[dateStr]) map[dateStr].push(order);
    });
    return map;
  }, [orders, weekDates]);

  const weeklyOrders = React.useMemo(() => {
    return weekDates.flatMap(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return ordersByDate[dateStr] || [];
    });
  }, [weekDates, ordersByDate]);

  const hasDirtyOrders = React.useMemo(
    () => orders.some(isOrderDirty),
    [orders, isOrderDirty]
  );

  // Calculate revenue per day
  const revenueByDate = React.useMemo(() => {
    const map = {};

    weekDates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayOrders = ordersByDate[dateStr] || [];

      map[dateStr] = dayOrders.reduce((sum, order) => {
        return sum + (Number(order.revenue) || 0);
      }, 0);
    });

    return map;
  }, [weekDates, ordersByDate]);

  // Calculate total weekly revenue
  const totalWeeklyRevenue = React.useMemo(() => {
    return Object.values(revenueByDate).reduce((sum, val) => sum + val, 0);
  }, [revenueByDate]);

  const progressBoxStyle = (status) => {
    let backgroundColor = '#e9ecef'; // default gray

    if (status === 'complete') {
      backgroundColor = '#198754'; // Bootstrap success
    } else if (status === 'partial') {
      backgroundColor = '#ffc107'; // Bootstrap warning
    }

    return {
      height: '24px',
      margin: '2px',
      borderRadius: '4px',
      backgroundColor,
      transition: 'background-color 0.3s ease'
    };
  };

  const handlePrint = React.useCallback(() => {
      window.print();
  }, []);

  const formatDateSafe = React.useCallback((dateLike) => {
    if (!dateLike) return 'N/A';
    const d = new Date(dateLike);
    return isNaN(d.getTime())
      ? 'N/A'
      : d.toLocaleDateString('en-CA', {
          timeZone: 'UTC',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
  }, []);

  const handleCopySchedule = React.useCallback(() => {
    if (!weeklyOrders || weeklyOrders.length === 0) {
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('default'), 1500);
      return;
    }

    const headers = [
      'Order Type', 'Dealer Name', 'Customer Name', 'Revenue', 'Order Date',
      'Paid Date', 'Pre-Production Received', 'Manufacturing Received',
      'Build Started', 'Completion Date', 'Ship/Pickup Date',
    ];

    const safeString = (value) =>
      String(value || '')
        .replace(/\t|\n|\r/g, ' ')
        .replace(/,/g, '');

    const dataRows = weeklyOrders.map(order => {
      const paidDate = order?.administration?.paymentCompletedDate;
      const preProdRec = order?.administration?.completionDate;
      const manufacturingRec = order?.preProduction?.completionDate;
      const productionDate = order?.manufacturing?.orderEnteredProductionDate;
      const shipDate = order?.shipping?.shippedDate;

      const revenueValue =
        typeof order.revenue === 'number'
          ? order.revenue.toFixed(2)
          : 'N/A';

      return [
        safeString(order.orderType),
        safeString(order.dealerName),
        safeString(order.customerName),
        revenueValue,
        formatDateSafe(order.orderDate),
        formatDateSafe(paidDate),
        formatDateSafe(preProdRec),
        formatDateSafe(manufacturingRec),
        formatDateSafe(productionDate),
        formatDateSafe(order.completionDate),
        formatDateSafe(shipDate),
      ].join('\t');
    });

    const tsvData = [headers.join('\t'), ...dataRows].join('\n');

    navigator.clipboard.writeText(tsvData)
      .then(() => {
        setCopyStatus('copied');
        setTimeout(() => setCopyStatus('default'), 2000);
      })
      .catch(() => {
        setCopyStatus('error');
        setTimeout(() => setCopyStatus('default'), 2000);
      });

  }, [weeklyOrders, formatDateSafe]);

  const getCopyButtonVariant = () => {
    switch (copyStatus) {
      case 'copied': return 'secondary';
      case 'error': return 'danger';
      default: return 'outline-dark';
    }
  };

  // Determine colSpan based on which view is active
  const totalColumns = 16

  return (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Header className="bg-white py-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-4">
                <h4 className="mb-0 text-primary">Currently Viewing:</h4>
                <small className="text-muted fw-bold">
                {format(weekDates[0], 'MMMM do, yyyy')} — {format(weekDates[5], 'MMMM do, yyyy')}
                </small>
            </div>

            <div className="text-end">
              <div className="fw-bold text-success fs-5">
                ${totalWeeklyRevenue.toLocaleString()}
              </div>
              <small className="text-muted">Weekly Revenue</small>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant={getCopyButtonVariant()}
                size="sm"
                onClick={handleCopySchedule}
                disabled={copyStatus !== 'default' || weeklyOrders.length === 0}
                className="px-2"
                title={
                  copyStatus === 'copied'
                    ? 'Copied!'
                    : copyStatus === 'error'
                    ? 'Failed to copy'
                    : 'Copy Weekly Schedule'
                }
              >
                <i className="bi bi-copy fs-5"></i>
              </Button>

              <Button
                variant="outline-dark"
                size="sm"
                onClick={handlePrint}
                className="px-2"
                title="Print Schedule"
              >
                <i className="bi bi-printer fs-5"></i>
              </Button>

              <Button
                variant={hasDirtyOrders ? 'success' : 'outline-secondary'}
                disabled={!hasDirtyOrders}
                size="sm"
                onClick={onSaveChanges}
                className="px-3"
              >
                <i className="bi bi-cloud-upload me-1"></i>
                Save All Changes
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => setShowProgress(prev => !prev)}
              >
                {showProgress ? (
                  <><i className="bi bi-info-circle me-1"></i> View Order Details</>
                ) : (
                  <><i className="bi bi-bar-chart-steps me-1"></i> View Progress</>
                )}
              </Button>
            </div>
        </div>
      </Card.Header>

      <Card.Body className="bg-light">
        {weekDates.map(date => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const dayOrders = ordersByDate[dateStr] || [];
          const isToday = isSameDay(today, date);

          return (
            <div key={dateStr} className="mb-4">
              <div className="d-flex align-items-center mb-2">
                <h5 className={`mb-0 ${isToday ? 'text-danger' : 'text-dark'}`}>
                  {format(date, 'EEEE, MMMM d')}
                </h5>
                {isToday && <Badge bg="danger" className="ms-2">TODAY</Badge>}
                <div className="ms-auto">
                  <Badge bg="secondary">{dayOrders.length} Orders</Badge>
                </div>
              </div>

              {dayOrders.length === 0 ? (
                <Card className="border-0 shadow-none bg-white-50 text-center py-3">
                  <small className="text-muted italic">No production scheduled for this day.</small>
                </Card>
              ) : (
                <div className="table-responsive shadow-sm rounded">
                  <Table bordered hover size="sm" className="mb-0 bg-white align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th className="text-center" style={{ width: '100px' }}>Order Type</th>
                        <th style={{ width: '140px' }}>Dealer Name</th>
                        <th style={{ width: '140px' }}>Customer Name</th>

                        {showProgress ? (
                          <>
                            <th className="text-center" style={{ width: '120px' }}>Stage</th>
                            <th className="text-center" style={{ width: '70px' }}>Frame</th>
                            <th className="text-center" style={{ width: '70px' }}>Brackets</th>
                            <th className="text-center" style={{ width: '70px' }}>Motor</th>
                            <th className="text-center" style={{ width: '70px' }}>Rods</th>
                            <th className="text-center" style={{ width: '70px' }}>R/Sup</th>
                            <th className="text-center" style={{ width: '70px' }}>Box</th>
                            <th className="text-center" style={{ width: '70px' }}>Upper/F</th>
                            <th className="text-center" style={{ width: '70px' }}>Lower/F</th>
                            <th className="text-center" style={{ width: '70px' }}>Extra/F</th>
                          </>
                        ) : (
                          <>
                            <th style={{ minWidth: '150px' }}>Custom Work</th>
                            <th style={{ width: '60px' }}>L</th>
                            <th style={{ width: '60px' }}>W</th>
                            <th style={{ width: '60px' }}>H</th>
                            <th style={{ width: '100px' }}>Frame</th>
                            <th style={{ width: '100px' }}>Upper Color</th>
                            <th style={{ width: '100px' }}>Lower Color</th>
                            <th style={{ width: '100px' }}>Brackets</th>
                          </>
                        )}

                        <th style={{ width: '100px' }}>Revenue</th>
                        <th style={{ width: '100px' }}>Order Date</th>
                        <th className="text-center hide-on-print" style={{ width: '150px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                        {dayOrders.map(order => {
                            const dirty = isOrderDirty(order);
                            const isExpanded = !!expandedRows[order.orderId];

                            const getBadgeVariant = (stage) => {
                                if (stage === 'Manufacturing') return 'warning text-dark';
                                if (stage === 'Shipping') return 'success';
                                if (stage === 'Completed') return 'dark';
                                return 'secondary';
                            };

                            const m = order.manufacturing || {};

                            // Frame Logic for Tasks
                            const isNewCover = order.orderType === 'New Cover';
                            const upperDone = !!m.upperFrameCompleted;
                            const lowerDone = !!m.lowerFrameCompleted;

                            let frameStatus = 'none';

                            if (isNewCover) {
                              if (upperDone && lowerDone) {
                                frameStatus = 'complete';
                              } else if (upperDone || lowerDone) {
                                frameStatus = 'partial';
                              }
                            } else if (upperDone || lowerDone) {
                              // Non–New Cover orders only need one frame completed
                              frameStatus = 'complete';
                            }

                            // Upper Fabric Logic for Tasks
                            const upperComplete = m.upperFabricBoxedUp === true;
                            const upperPartial =
                              m.upperAndLowerFabricsDrawingsCompleted === true ||
                              m.upperAndLowerFabricsCut === true ||
                              m.upperFabricToFit === true ||
                              m.upperFabricInProgress === true ||
                              m.upperFabricCompleted === true;

                            let upperFabricStatus = 'none';

                            if (upperComplete) {
                              upperFabricStatus = 'complete';
                            } else if (upperPartial) {
                              upperFabricStatus = 'partial';
                            }

                            // Lower Fabric Logic for Tasks
                            const lowerComplete = m.lowerFabricAndCableLinersBoxedUp === true;
                            const lowerPartial =
                              m.upperAndLowerFabricsDrawingsCompleted === true ||
                              m.upperAndLowerFabricsCut === true ||
                              m.lowerFabricInProgress === true ||
                              m.lowerFabricCompleted === true;

                            let lowerFabricStatus = 'none';

                            if (lowerComplete) {
                              lowerFabricStatus = 'complete';
                            } else if (lowerPartial) {
                              lowerFabricStatus = 'partial';
                            }

                            // Extra Fabric Components Logic
                            // 1. Define the pairs so the logic knows which "InProgress" belongs to which "Completed"
                            const fabricComponentGroups = [
                              { partial: 'chafePanelsInProgress', complete: 'chafePanelsCompleted' },
                              { partial: 'pileCoversInProgress', complete: 'pileCoversCompleted' },
                              { partial: 'fabricSkirtsInProgress', complete: 'fabricSkirtsCompleted' },
                              { partial: 'birdPanelsInProgress', complete: 'birdPanelsCompleted' },
                              { partial: 'dropPanelsInProgress', complete: 'dropPanelsCompleted' }
                            ];

                            // 2. Identify which groups have any activity at all
                            const activeGroups = fabricComponentGroups.filter(group => m[group.partial] || m[group.complete]);

                            // 3. Determine the status based on the active groups
                            let extraStatus = 'none';

                            if (activeGroups.length > 0) {
                              // Check if every single active group has its "complete" flag checked
                              const allActiveAreFinished = activeGroups.every(group => !!m[group.complete]);

                              if (allActiveAreFinished) {
                                extraStatus = 'complete'; // Green
                              } else {
                                extraStatus = 'partial';  // Yellow
                              }
                            }
                            
                            return (
                            <React.Fragment key={order.orderId}>
                                <tr className={dirty ? 'table-warning' : ''}>
                                <td className="text-center">
                                    {dirty && <span className="text-danger me-1 fw-bold" title="Unsaved changes">*</span>}
                                    {order.orderType || 'N/A'}
                                </td>
                                <td style={{ maxWidth: '140px' }}>{order.dealerName}</td>
                                <td style={{ maxWidth: '140px' }}>{order.customerName}</td>

                                {showProgress ? (
                                    <>
                                    {/* Stage Badge (Before Progress Bars) */}
                                    <td className="text-center">
                                        <Badge bg={getBadgeVariant(order.currentStage)} className="text-uppercase" style={{ fontSize: '0.7rem' }}>
                                            {order.currentStage}
                                        </Badge>
                                    </td>

                                    {/* Progress visualization cells */}
                                    {/* Frame */}
                                    <td className="text-center p-0">
                                      <div style={progressBoxStyle(frameStatus)} />
                                    </td>

                                    {/* Brackets */}
                                    <td className="text-center p-0">
                                      <div
                                        style={progressBoxStyle(
                                          order.manufacturing?.bracketsCompleted === true
                                            ? 'complete'
                                            : 'none'
                                        )}
                                      />
                                    </td>

                                    {/* Motor */}
                                    <td className="text-center p-0">
                                      <div
                                        style={progressBoxStyle(
                                          order.manufacturing?.motorCompleted === true
                                            ? 'complete'
                                            : 'none'
                                        )}
                                      />
                                    </td>

                                    {/* Rods */}
                                    <td className="text-center p-0">
                                      <div
                                        style={progressBoxStyle(
                                          order.manufacturing?.fiberglassRodsCompleted === true
                                            ? 'complete'
                                            : 'none'
                                        )}
                                      />
                                    </td>

                                    {/* R/Sup */}
                                    <td className="text-center p-0">
                                      <div
                                        style={progressBoxStyle(
                                          order.manufacturing?.ridgeSupportsCompleted === true
                                            ? 'complete'
                                            : 'none'
                                        )}
                                      />
                                    </td>

                                    {/* Box */}
                                    <td className="text-center p-0">
                                      <div
                                        style={progressBoxStyle(
                                          order.manufacturing?.hardwareKitCompleted === true
                                            ? 'complete'
                                            : 'none'
                                        )}
                                      />
                                    </td>

                                    {/* Upper/F */}
                                    <td className="text-center p-0">
                                      <div style={progressBoxStyle(upperFabricStatus)} />
                                    </td>

                                    {/* Lower/F */}
                                    <td className="text-center p-0">
                                      <div style={progressBoxStyle(lowerFabricStatus)} />
                                    </td>

                                    {/* Extra/F */}
                                    <td className="text-center p-0">
                                      <div style={progressBoxStyle(extraStatus)} />
                                    </td>

                                    </>
                                ) : (
                                    <>
                                    <td className="small text-muted">{order.customWork || '---'}</td>
                                    <td>{order.length ?? '-'}</td>
                                    <td>{order.width ?? '-'}</td>
                                    <td>{order.height ?? '-'}</td>
                                    <td><small>{order.frameType || '-'}</small></td>
                                    <td><small>{order.upperColor || '-'}</small></td>
                                    <td><small>{order.lowerColor || '-'}</small></td>
                                    <td><small>{order.brackets || '-'}</small></td>
                                    </>
                                )}

                                <td className="fw-bold text-success">
                                    {order.revenue ? `$${order.revenue.toLocaleString()}` : '$0'}
                                </td>

                                 <td>
                                    <small>
                                        {order.orderDate
                                        ? format(new Date(order.orderDate), 'MMM d, yyyy')
                                        : '-'}
                                    </small>
                                </td>
                                
                                <td className="text-center hide-on-print">
                                    <div className="d-flex flex-column align-items-center gap-2">

                                        {/* FIRST ROW: MARK AS DONE & SEND */}
                                        <Button
                                        variant={
                                              order.currentStage !== 'Manufacturing'
                                                ? 'secondary'
                                                : 'outline-success'
                                            }
                                        size="sm"
                                        title={
                                            order.currentStage === 'Manufacturing'
                                            ? 'Complete Order & Send'
                                            : 'Order already advanced'
                                        }
                                        className="w-100"
                                        onClick={() => onMarkAsDone(order)}
                                        disabled={order.currentStage !== 'Manufacturing'}
                                        >
                                        Mark as Done & Send
                                        </Button>

                                        {/* SECOND ROW: Change Date + Unschedule (stacked horizontally) */}
                                        <div className="d-flex gap-2 w-100">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            title="Reschedule / Change Date"
                                            onClick={() => onChangeDate(order)}
                                            className="w-100"
                                        >
                                            Change Date
                                        </Button>

                                        <Button
                                            variant={
                                              order.currentStage !== 'Manufacturing'
                                                ? 'secondary'
                                                : 'outline-danger'
                                            }
                                            size="sm"
                                            title="Remove from Schedule (Move back to Backlog)"
                                            onClick={() => onUnschedule(order)}
                                            className="w-100"
                                            disabled={order.currentStage !== 'Manufacturing'}
                                        >
                                            Unschedule
                                        </Button>
                                        </div>

                                        {/* THIRD ROW: TASKS */}
                                        <Button
                                        variant={
                                              order.currentStage === 'Completed'
                                                ? 'secondary'
                                                : 'primary'
                                            }
                                        size="sm"
                                        onClick={() => toggleRow(order.orderId)}
                                        style={{ minWidth: '60px' }}
                                        disabled={order.currentStage === 'Completed'}
                                        className="w-100"
                                        >
                                        {isExpanded ? 'Close Tasks' : 'View Tasks'}
                                        </Button>

                                    </div>
                                </td>
                                </tr>

                                {/* Task Expansion Logic */}
                                {isExpanded && renderExpandedRow(order, totalColumns)}
                            </React.Fragment>
                            );
                        })}
                        
                        {/* Daily Revenue Total Row */}
                        <tr className="table-light no-border-row">
                          <td colSpan={showProgress ? (totalColumns - 5) : (totalColumns - 7)}></td>

                          <td className="fw-bold text-end">
                            Daily
                          </td>

                          <td className="fw-bold text-start">
                            Total:
                          </td>

                          <td className="fw-bold text-success">
                            ${revenueByDate[dateStr]?.toLocaleString() || '0'}
                          </td>

                          {/* Column Spacers */}
                          <td></td>
                          <td className='hide-on-print'></td>
                        </tr>
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}